import '@/utils/nextui-overlay-defaults';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import './sentry.ts';
import { isEmbeddedContext, getAuth0RedirectUri, isTeamsContext } from './utils/auth-context';
import {
  isTeamsAuthPopupSession,
  notifyTeamsAuthSuccess,
  TEAMS_AUTH_POPUP_STATE_KEY,
  type TeamsAuthAppState,
} from './utils/teams-auth';

const isEmbedded = isEmbeddedContext();
const isTeams = isTeamsContext();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});
// import { getTenantNane } from './utils/tenantInfo';

// ... existing comment block ...

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Auth0Provider
        domain={import.meta.env['VITE_AUTH0_DOMAIN']}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        {...(isEmbedded
          ? {
              useCookiesForTransactions: true,
              ...(isTeams
                ? {
                    // Teams SSO popup shares Auth0 cache with the parent tab via localStorage.
                    cacheLocation: 'localstorage' as const,
                  }
                : {}),
            }
          : {})}
        authorizationParams={{
          redirect_uri: getAuth0RedirectUri(),
          ...(import.meta.env['VITE_AUTH0_AUDIENCE']
            ? { audience: import.meta.env['VITE_AUTH0_AUDIENCE'] }
            : {}),
        }}
        onRedirectCallback={(appState) => {
          const state = appState as TeamsAuthAppState | undefined;

          if (
            state?.[TEAMS_AUTH_POPUP_STATE_KEY] ||
            isTeamsAuthPopupSession()
          ) {
            notifyTeamsAuthSuccess();
            return;
          }

          const returnTo = state?.returnTo || window.location.pathname;
          window.history.replaceState({}, document.title, returnTo);
        }}
      >
        <Router>
          <App />
        </Router>
      </Auth0Provider>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  </StrictMode>
);
